import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { profileApi, authApi } from '../services/api';
import api from '../services/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Loader2, Save, Lock, Eye, EyeOff, Trash2, AlertTriangle } from 'lucide-react';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { extractAxiosError } from '../components/SafeError';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const SIMULATORS = ['MSFS 2020', 'MSFS 2024'];
const AIRCRAFT = ['Fenix A320', 'iniBuilds A320', 'Asobo A320'];
const NAV_SOURCES = ['Navigraph', 'Default'];
const ATC_PLATFORMS = ['VATSIM', 'SayIntentions', 'BeyondATC', 'MSFS ATC', 'None'];
// IMPORTANT: 'name' is the canonical value saved to backend and used for filtering
// Only supported integrations that affect training content
const ADDONS = [
  { id: 'GSX Pro', name: 'GSX Pro', description: 'Ground Services & Pushback' },
  { id: 'SimBrief', name: 'SimBrief', description: 'Flight Planning & Dispatch' },
];

const SettingsPage = () => {
  const { user, profile, setProfile, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [displayNameLoading, setDisplayNameLoading] = useState(false);

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const [formData, setFormData] = useState({
    sim_version: profile?.sim_version || '',
    aircraft_variant: profile?.aircraft_variant || '',
    nav_source: profile?.nav_source || '',
    atc_platform: profile?.atc_platform || '',
    addons: profile?.addons || [],
  });

  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Sync formData when profile loads/changes (handles async profile loading)
  React.useEffect(() => {
    if (profile) {
      setFormData({
        sim_version: profile.sim_version || '',
        aircraft_variant: profile.aircraft_variant || '',
        nav_source: profile.nav_source || '',
        atc_platform: profile.atc_platform || '',
        addons: profile.addons || [],
      });
    }
  }, [profile]);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSuccess('');
  };

  // Toggle addon by canonical name (e.g., "GSX Pro") - this is what backend expects
  const toggleAddon = (addonName) => {
    setFormData(prev => ({
      ...prev,
      addons: prev.addons.includes(addonName)
        ? prev.addons.filter(name => name !== addonName)
        : [...prev.addons, addonName]
    }));
    setSuccess('');
  };

  const handleDisplayNameSave = async () => {
    if (!displayName.trim() || displayName.trim().length < 2) {
      setError('Name must be at least 2 characters');
      return;
    }
    try {
      setDisplayNameLoading(true);
      setError('');
      await api.put('/auth/display-name', { display_name: displayName.trim() });
      await refreshUser();
      setSuccess('Name updated successfully');
    } catch (err) {
      setError(extractAxiosError(err, 'Failed to update name'));
    } finally {
      setDisplayNameLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validation
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    try {
      setPasswordLoading(true);
      await authApi.changePassword({ 
        current_password: passwordData.currentPassword, 
        new_password: passwordData.newPassword 
      });
      setPasswordSuccess('Password changed successfully');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPasswordError(extractAxiosError(err, 'Failed to change password'));
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const { data } = await profileApi.update(formData);
      setProfile(data);
      await refreshUser();
      setSuccess('Settings saved successfully');
    } catch (err) {
      setError(extractAxiosError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    try {
      setDeleteLoading(true);
      await api.delete('/auth/delete-account');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/';
    } catch (err) {
      setError(extractAxiosError(err, 'Failed to delete account'));
      setDeleteLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl" data-testid="settings-page">
        <h1 className="text-2xl font-semibold text-cockpit-text-primary mb-6">Settings</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Success message */}
          {success && (
            <div className="p-3 bg-status-success/20 border border-status-success/50 rounded-sm">
              <p className="text-sm text-status-success" data-testid="settings-success">{success}</p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="p-3 bg-red-900/30 border border-red-900 rounded-sm">
              <p className="text-sm text-red-300" data-testid="settings-error">{error}</p>
            </div>
          )}

          {/* Preferred Name Section */}
          <div className="card-panel p-6">
            <h2 className="text-lg font-medium text-cockpit-text-primary mb-4">Personal</h2>
            
            <div className="space-y-4">
              <div>
                <Label className="label-text block mb-2">Preferred Name</Label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="input-field flex-1"
                    placeholder="What should we call you?"
                    maxLength={50}
                    data-testid="settings-display-name"
                  />
                  <button
                    type="button"
                    onClick={handleDisplayNameSave}
                    disabled={displayNameLoading || displayName === user?.display_name}
                    className="px-4 py-2 bg-cockpit-border text-cockpit-text-secondary rounded-sm hover:bg-cockpit-border/80 disabled:opacity-50 transition-colors text-sm"
                    data-testid="settings-display-name-save"
                  >
                    {displayNameLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update'}
                  </button>
                </div>
                <p className="text-xs text-cockpit-text-secondary/50 mt-1">
                  This is how you'll be greeted in the app.
                </p>
              </div>
            </div>
          </div>

          {/* Password Change Section */}
          <div className="card-panel p-6">
            <h2 className="text-lg font-medium text-cockpit-text-primary mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Change Password
            </h2>
            
            {passwordError && (
              <div className="mb-4 p-3 bg-red-900/30 border border-red-900 rounded-sm text-sm text-red-300">
                {passwordError}
              </div>
            )}
            
            {passwordSuccess && (
              <div className="mb-4 p-3 bg-green-900/30 border border-green-900 rounded-sm text-sm text-green-300">
                {passwordSuccess}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <Label className="label-text block mb-2">Current Password</Label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                    className="input-field w-full pr-10"
                    placeholder="Enter current password"
                    data-testid="settings-current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-cockpit-text-secondary hover:text-cockpit-text-primary"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label className="label-text block mb-2">New Password</Label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                    className="input-field w-full pr-10"
                    placeholder="Enter new password (min 8 characters)"
                    data-testid="settings-new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-cockpit-text-secondary hover:text-cockpit-text-primary"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label className="label-text block mb-2">Confirm New Password</Label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  className="input-field w-full"
                  placeholder="Confirm new password"
                  data-testid="settings-confirm-password"
                />
              </div>

              <button
                type="button"
                onClick={handlePasswordChange}
                disabled={passwordLoading}
                className="btn-secondary flex items-center gap-2"
                data-testid="settings-change-password"
              >
                {passwordLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                {passwordLoading ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={loading}
            className="btn-primary flex items-center gap-2"
            data-testid="settings-save"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </form>

        {/* Delete Account Section — hidden for founder, outside the form */}
        {user?.role !== 'founder' && (
        <div className="mt-10 pt-8 border-t border-red-900/30">
          <div className="card-panel p-6 border-red-900/50">
            <h2 className="text-lg font-medium text-red-400 mb-2 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Delete Account
            </h2>
            <p className="text-sm text-cockpit-text-secondary/70 mb-4">
              Permanently delete your account and all associated data. This action cannot be undone.
            </p>

            {!showDeleteConfirm ? (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="px-4 py-2 bg-red-900/30 border border-red-900/50 text-red-400 rounded-sm hover:bg-red-900/50 transition-colors text-sm"
                data-testid="settings-delete-account-btn"
              >
                Delete My Account
              </button>
            ) : (
              <div className="p-4 bg-red-950/30 border border-red-900/50 rounded-sm space-y-4" data-testid="delete-account-confirm-dialog">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-red-400 mb-1">This is permanent</p>
                    <p className="text-xs text-cockpit-text-secondary/70">
                      Your account, settings, progress and all associated data will be permanently erased. 
                      If you have an active subscription, it will not be refunded. This cannot be reversed.
                    </p>
                  </div>
                </div>

                <div>
                  <Label className="label-text block mb-2 text-xs">
                    Type <span className="font-mono text-red-400">DELETE</span> to confirm
                  </Label>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    className="input-field w-full"
                    placeholder="Type DELETE"
                    data-testid="delete-account-confirm-input"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleDeleteAccount}
                    disabled={deleteConfirmText !== 'DELETE' || deleteLoading}
                    className="px-4 py-2 bg-red-700 text-white rounded-sm hover:bg-red-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-sm flex items-center gap-2"
                    data-testid="delete-account-confirm-btn"
                  >
                    {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    {deleteLoading ? 'Deleting...' : 'Permanently Delete Account'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); }}
                    className="px-4 py-2 bg-cockpit-border text-cockpit-text-secondary rounded-sm hover:bg-cockpit-border/80 transition-colors text-sm"
                    data-testid="delete-account-cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;
